import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarX2, CalendarCheck } from "lucide-react";
import { isNewOrder } from "@/lib/orderDetails";
import { OrderCard } from "@/components/admin/OrderCard";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DayCell = ({ day, list, blocked, today, selected, onPick }) => {
  const hasNew = list.some(isNewOrder);
  return (
    <button data-testid={`admin-cal-day-${day}`} onClick={() => onPick(day)}
      className={`relative flex aspect-square flex-col items-start rounded-xl border p-1.5 text-left transition-colors sm:p-2 ${selected ? "border-sugar-ink ring-2 ring-sugar-ink" : "border-sugar-border"} ${blocked ? "bg-[repeating-linear-gradient(45deg,#f3eef0,#f3eef0_6px,#fff_6px,#fff_12px)]" : hasNew ? "bg-yellow-200" : "bg-white hover:bg-sugar-cream"}`}>
      <span className={`text-xs font-bold ${today ? "grid h-6 w-6 place-items-center rounded-full bg-sugar-pink text-white" : "text-sugar-ink"}`}>{Number(day.slice(-2))}</span>
      {list.length > 0 && (
        <span data-testid={`admin-cal-count-${day}`} className={`mt-auto rounded-full px-1.5 py-0.5 text-[10px] font-extrabold ${hasNew ? "bg-sugar-ink text-yellow-300" : "bg-sugar-pink text-white"}`}>
          {list.length}<span className="hidden sm:inline"> order{list.length > 1 ? "s" : ""}</span>
        </span>
      )}
    </button>
  );
};

export const AdminCalendar = ({ orders, blocked, onToggleBlock }) => {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [selected, setSelected] = useState(ymd(now));
  const byDay = useMemo(() => {
    const m = {};
    orders.filter((o) => o.payment_status !== "pending").forEach((o) => { (m[o.fulfillment_date] = m[o.fulfillment_date] || []).push(o); });
    return m;
  }, [orders]);
  const cells = useMemo(() => {
    const first = month.getDay();
    const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => ymd(new Date(month.getFullYear(), month.getMonth(), i + 1)))];
  }, [month]);
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const dayOrders = byDay[selected] || [];
  const isBlocked = blocked.includes(selected);
  const upcoming = Object.keys(byDay).filter((d) => d >= ymd(now)).sort().slice(0, 6);

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div className="rounded-2xl border border-sugar-border bg-white p-4 sm:p-5">
        <div className="flex items-center justify-between">
          <button data-testid="admin-cal-prev" onClick={() => shift(-1)} className="grid h-9 w-9 place-items-center rounded-full border border-sugar-border hover:bg-sugar-cream"><ChevronLeft size={18} /></button>
          <h3 data-testid="admin-cal-month" className="font-display text-lg font-bold text-sugar-ink">{month.toLocaleString("en-CA", { month: "long", year: "numeric" })}</h3>
          <button data-testid="admin-cal-next" onClick={() => shift(1)} className="grid h-9 w-9 place-items-center rounded-full border border-sugar-border hover:bg-sugar-cream"><ChevronRight size={18} /></button>
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1 sm:gap-1.5">
          {DOW.map((d) => <div key={d} className="pb-1 text-center text-[10px] font-bold uppercase tracking-wider text-sugar-muted">{d}</div>)}
          {cells.map((d, i) => d
            ? <DayCell key={d} day={d} list={byDay[d] || []} blocked={blocked.includes(d)} today={d === ymd(now)} selected={d === selected} onPick={setSelected} />
            : <div key={`e${i}`} />)}
        </div>
        <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-sugar-ink2">
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-yellow-200" /> Has new orders</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-sugar-pink" /> Orders booked</span>
          <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-[repeating-linear-gradient(45deg,#e5dde0,#e5dde0_3px,#fff_3px,#fff_6px)]" /> Blocked</span>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3">
          <h3 data-testid="admin-cal-selected" className="font-display text-lg font-bold text-sugar-ink">{new Date(selected + "T12:00:00").toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric" })}</h3>
          <button data-testid="admin-cal-toggle-block" onClick={() => onToggleBlock(selected, !isBlocked)} className="inline-flex items-center gap-1.5 rounded-full border border-sugar-border bg-white px-3 py-1.5 text-xs font-bold text-sugar-ink2 hover:border-sugar-pink hover:text-sugar-pink">
            {isBlocked ? <><CalendarCheck size={14} /> Open this day</> : <><CalendarX2 size={14} /> Block this day</>}
          </button>
        </div>
        <div className="mt-3 space-y-3">
          {dayOrders.length === 0 && <p className="rounded-2xl border border-dashed border-sugar-border bg-white p-6 text-center text-sm text-sugar-ink2">No orders on this day{isBlocked ? " — it's blocked for customers." : "."}</p>}
          {dayOrders.map((o) => <OrderCard key={o.id} o={o} />)}
        </div>
        {upcoming.length > 0 && (
          <div className="mt-6">
            <h4 className="text-xs font-bold uppercase tracking-widest text-sugar-pink">Coming up</h4>
            <div className="mt-2 flex flex-wrap gap-2">
              {upcoming.map((d) => (
                <button key={d} data-testid={`admin-cal-upcoming-${d}`} onClick={() => { setSelected(d); setMonth(new Date(d.slice(0, 4), Number(d.slice(5, 7)) - 1, 1)); }}
                  className="rounded-full border border-sugar-border bg-white px-3 py-1.5 text-xs font-semibold text-sugar-ink2 hover:border-sugar-pink">
                  {new Date(d + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" })} · {byDay[d].length}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
